import { Events } from './events';
import { uploadScene, isFirebaseConfigured } from './firebase';

export type ShareResult = {
    sceneUrl: string;     // public URL of the uploaded .ssproj
    viewerUrl: string;    // editor.simamdigital.com/?view&load=<sceneUrl>
    iframe: string;       // ready-to-paste <iframe> embed snippet
    sizeBytes?: number;   // size of the published scene (when known)
};

// build the branded viewer URL for a hosted scene file. An explicit `filename`
// hint (defaulting to a clean .ssproj name) keeps file-type detection robust even
// when the hosted URL carries query tokens (Firebase ?alt=media&token=…).
const buildViewerUrl = (sceneUrl: string, filename = 'experience.ssproj') => {
    return `${location.origin}/?view&load=${encodeURIComponent(sceneUrl)}` +
        `&filename=${encodeURIComponent(filename)}`;
};

// build a ready-to-paste responsive iframe embed snippet
const buildIframe = (viewerUrl: string) => {
    return `<iframe
    src="${viewerUrl}"
    width="100%"
    height="600"
    style="border:0;border-radius:12px;width:100%;aspect-ratio:16/9;height:auto"
    allow="fullscreen; accelerometer; gyroscope; xr-spatial-tracking"
    allowfullscreen
    loading="lazy"
    title="Simam 3D Studio experience"></iframe>`;
};

const registerShareEvents = (events: Events) => {
    events.function('share.configured', () => isFirebaseConfigured());

    // build a viewer url + iframe for an already-hosted scene (no upload)
    events.function('share.fromUrl', (sceneUrl: string): ShareResult => {
        const viewerUrl = buildViewerUrl(sceneUrl);
        return { sceneUrl, viewerUrl, iframe: buildIframe(viewerUrl) };
    });

    // serialize the current scene, upload it, and return the share artifacts
    events.function('share.publish', async (opts?: { name?: string }): Promise<ShareResult> => {
        if (!isFirebaseConfigured()) {
            throw new Error('Publishing is not configured. Add your Firebase web config in src/firebase-config.ts.');
        }

        events.fire('progressStart', 'Publishing experience…');
        try {
            events.fire('progressUpdate', { text: 'Packaging scene…', progress: 5 });

            const bytes = await events.invoke('doc.serializeToBytes') as Uint8Array;
            if (!bytes || bytes.length === 0) {
                throw new Error('Failed to package the scene.');
            }

            const name = opts?.name || events.invoke('doc.name') || 'experience';

            const sceneUrl = await uploadScene(bytes, name, (loaded, total) => {
                events.fire('progressUpdate', {
                    text: 'Uploading…',
                    progress: total ? 10 + 85 * loaded / total : 50
                });
            });

            events.fire('progressUpdate', { text: 'Done', progress: 100 });

            const viewerUrl = buildViewerUrl(sceneUrl);
            return { sceneUrl, viewerUrl, iframe: buildIframe(viewerUrl), sizeBytes: bytes.length };
        } finally {
            events.fire('progressEnd');
        }
    });
};

export { registerShareEvents, buildViewerUrl, buildIframe };
